// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 17b9014d-ddde-4f7a-9c0b-643d928da8e5

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// The master landing. The optimum workspace deliberately holds no records of
// its own, and for a while that meant a master signed in to blank screens that
// read as a broken product — the owner's ask, verbatim, was "I thought we had
// the master info in optimum, not blank": per-client dates and updates, a nudge
// when something changes inside a client, and links to the master list and the
// admin table. This screen is that. It renders ONLY for a master sitting in the
// optimum workspace; entering any client portal lands on the ordinary
// per-workspace Dashboard exactly as before.
//
// Every number comes from ONE call to /master/overview, aggregated in SQL —
// nothing here fetches fact sheets, so the master landing stays fast no matter
// how large the largest client grows, and it works even when the browser's
// local store is stale or over quota (the bug that made master screens lie).

import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Grid, Table, TableHead, TableRow, TableCell,
  TableBody, Button, Chip, Alert, Avatar, Link,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/ArrowForward';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpBubble from './HelpBubble';
import PrimordiaLogo from './PrimordiaLogo';
import { getConfig } from './config';
import { getIdToken } from './auth';

const BLUE = '#0D5B9A';

function Kpi({ label, value, sub, action }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="overline" sx={{ color: '#666' }}>{label}</Typography>
      <Typography variant="h3" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>{value}</Typography>
      {sub && <Typography variant="body2" sx={{ color: '#888' }}>{sub}</Typography>}
      {action}
    </Paper>
  );
}

const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return d.toLocaleDateString();
};

export default function MasterDashboard({ onOpenTenant, onNavigate }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const [waking, setWaking] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const attempt = async () => {
        const res = await fetch(`${getConfig().apiUrl}/master/overview`, {
          headers: { Authorization: `Bearer ${getIdToken()}` },
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.json();
      };
      try {
        let body;
        try {
          body = await attempt();
        } catch (first) {
          // The dev database auto-pauses when idle; the first request of the
          // day can outlast even the server's patience. Say so honestly and
          // try once more rather than showing an error for a cluster that is
          // simply stretching awake.
          if (!alive) return;
          setWaking(true);
          await new Promise((r) => { setTimeout(r, 6000); });
          body = await attempt();
        }
        if (alive) { setData(body); setWaking(false); }
      } catch (e) {
        // No fallback to local/seed data ON PURPOSE: a master overview built
        // from a stale cache is the exact "fake data believed real" failure
        // this codebase has already shipped once. An error is the honest state.
        if (alive) setErr('The overview could not be loaded from the API. Refresh to retry; if it persists, the API itself is the place to look.');
      }
    })();
    return () => { alive = false; };
  }, []);

  if (err) {
    return (
      <Box sx={{ p: 3 }}><Alert severity="error">{err}</Alert></Box>
    );
  }
  if (!data) {
    return (
      <Box sx={{ minHeight: '50vh', display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
        <PrimordiaLogo size={48} spinning />
        {waking && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Waking the database — the dev environment naps when idle. A few more seconds.
          </Typography>
        )}
      </Box>
    );
  }

  const active = data.tenants.filter((t) => t.status !== 'archived');
  const archived = data.tenants.length - active.length;
  const totalRecords = active.reduce((n, t) => n + (t.factSheets || 0), 0);
  const busy7d = active.filter((t) => t.changes7d > 0);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>Master overview</Typography>
        <HelpBubble topic="masterDashboard" />
      </Stack>
      <Typography sx={{ fontSize: 13, color: '#5C6B7A', mb: 2 }}>
        Every client at a glance. Enter a workspace to see its estate; nothing on this screen ever
        blends two clients into one number.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Kpi label="Active clients" value={active.length} sub={archived ? `plus ${archived} archived` : ' '} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Kpi label="Records under management" value={totalRecords.toLocaleString()} sub="live fact sheets, all clients" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Kpi
            label="Master catalog"
            value={data.catalog.entries}
            sub={`${data.catalog.unmapped} application names not yet reconciled`}
            action={(
              <Button size="small" startIcon={<LibraryBooksIcon />} onClick={() => onNavigate('catalog')} sx={{ mt: 0.5, color: BLUE }}>
                Open the master list
              </Button>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Kpi
            label="Administration"
            value={busy7d.length}
            sub="clients with changes this week"
            action={(
              <Button size="small" startIcon={<SettingsIcon />} onClick={() => onNavigate('admin')} sx={{ mt: 0.5, color: BLUE }}>
                Users, fields, companies
              </Button>
            )}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main', mb: 1 }}>Clients</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell align="right">Records</TableCell>
                  <TableCell align="right">Apps</TableCell>
                  <TableCell>Last change</TableCell>
                  <TableCell align="right">7 days</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.tenants.map((t) => (
                  <TableRow key={t.id} hover sx={t.status === 'archived' ? { opacity: 0.55 } : undefined}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: t.color || BLUE }}>
                          {t.name.charAt(0)}
                        </Avatar>
                        <span>{t.name}</span>
                        {t.status === 'archived' && <Chip size="small" label="archived" />}
                        {t.kind === 'sample' && <Chip size="small" label="demo" color="info" variant="outlined" />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{(t.factSheets || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{t.applications || 0}</TableCell>
                    <TableCell>{when(t.lastChangeAt)}</TableCell>
                    <TableCell align="right">
                      {t.changes7d > 0
                        ? <Chip size="small" color="warning" label={t.changes7d} />
                        : <Typography component="span" sx={{ fontSize: 12, color: '#9AA6B2' }}>quiet</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => onOpenTenant(t.id)} sx={{ color: BLUE }}>
                        Enter
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main', mb: 1 }}>Recent activity</Typography>
            {data.activity.length === 0 && (
              <Typography sx={{ fontSize: 13, color: '#9AA6B2' }}>Nothing recorded yet.</Typography>
            )}
            <Stack spacing={1}>
              {data.activity.map((a, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Box key={i} sx={{ borderLeft: `3px solid ${a.action === 'DELETE' ? '#D0021B' : BLUE}`, pl: 1.2 }}>
                  <Typography sx={{ fontSize: 13, color: 'sectionHeader.main' }}>
                    <b>{a.entityName || a.entityType}</b>
                    {' '}
                    {a.action.toLowerCase()}d in
                    {' '}
                    <Link component="button" underline="hover" onClick={() => onOpenTenant(a.tenantId)} sx={{ fontSize: 13, fontWeight: 700, verticalAlign: 'baseline' }}>
                      {a.tenantId}
                    </Link>
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#8A97A5' }}>
                    {when(a.at)}
                    {a.actor ? ` — ${a.actor}` : ''}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
